import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import LoadingButton from "@mui/lab/LoadingButton";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
  Stack,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import destinationSvg from "Assets/Images/Survey/destination-icon.svg";

import { Marker, Popup } from "react-leaflet";
import L, { MarkerCluster, icon } from "leaflet";
import { STATIONS } from "Constants/Stations";
import type { ITag } from "Types/tag";
import Station2 from "Pages/Home/Map/Station2";
import { useAppDispatch, useAppSelector } from "Hooks/useRedux";
import useStations from "Hooks/useStations";

import Api from "Api";

import { getLocationData } from "Helper/map";

import { Form, FormContainer, IInput } from "Components/Form";
import MapContainer2 from "Components/MapContainer2";
import Modal from "Components/Modal";

import { changeShowAddStationModal, setMinimize, toggleShowStationMarker } from "Redux/mapSlice";
import { setStation } from "Redux/singleLocationSlice";
import type { IChildrenProps } from ".";

interface IAttribute {
  name: string;
  value: string;
}

const INPUTS: IInput[] = [
  { name: "title", label: "نام ایستگاه", gridSize: { xs: 6 } },
  { name: "code", label: "کد ایستگاه (اختیاری)", gridSize: { xs: 6 } },
  { name: "subtitle", label: "زیرنویس ایستگاه (اختیاری)", gridSize: { xs: 6 } },
  { name: "tags", label: "تگ (با خط فاصله جدا شود)", gridSize: { xs: 6 } },
  { name: "address", label: "آدرس", gridSize: { xs: 12 } },
];
const ATTRIBUTE_INPUTS: IInput[] = [
  { name: "name", label: "عنوان" },
  { name: "value", label: "مقدار" },
];

const ICON_SIZE = 22;

const destinationIcon = icon({ iconUrl: destinationSvg, iconSize: [ICON_SIZE, ICON_SIZE] });

export default function AddStation({ closeModal, isOpen, modalData }: IChildrenProps) {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { type } = useAppSelector((state) => state.operation);
  const { showAddStationModal, stationLoc } = useAppSelector((state) => state.map);
  const { singleStationData } = useAppSelector((state) => state.singleLocation);

  const { invalidateQuery } = useStations();
  const { path } = useAppSelector((state) => state.app);

  // const stationType = type || modalData?.type;
  const stationType = modalData?.type;
  const stationData = STATIONS.filter((station) => station.type === stationType)[0];
  const isEdit = modalData?.isEdit || false;
  const modalText = isEdit ? "ویرایش ایستگاه" : "افزودن ایستگاه";

  const [formData, setFormData] = useState<{ [key: string]: any } | null>(null);
  const [attributes, setAttributes] = useState<IAttribute[]>([]);
  const [attributeForm, setAttributeForm] = useState<IAttribute>({ name: "", value: "" });
  const [lastLoc, setLastLoc] = useState<any>({ lat: 0, lng: 0, address: "" });
  const [editLocMode, setEditLocMode] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [stationPaths, setStationPaths] = useState<any[]>([]);
  const [pathRange, setPathRange] = useState<"all" | "one" | "">("one");

  // Logic functions
  const handleAddAttribute = () => {
    setAttributes((prev) => [...prev, attributeForm]);
    setAttributeForm({ name: "", value: "" });
  };
  const handleRemoveAttribute = (index: number) => {
    setAttributes((prev) => prev.filter((attribute, idx) => index !== idx));
  };
  const handleCloseModal = () => {
    setFormData(null);
    setAttributes([]);
    dispatch(changeShowAddStationModal(false));
    closeModal();
  };

  const addStationMutation = useMutation({
    mutationFn: () => {
      const tags = formData?.tags.length > 0 ? formData?.tags.split("-") : [];
      return Api.post("path/station", { ...formData, attributes, type, tags }).then((res) => {
        invalidateQuery();
        toast.success("ایستگاه با موفقیت اضافه شد");
        closeModal();
      });
    },
  });

  const editStationMutation = useMutation({
    mutationFn: () => {
      const tags = formData?.tags.length > 0 ? formData?.tags.split("-") : [];
      return Api.put(`path/station/${modalData.id}`, { ...formData, attributes, tags }).then((res) => {
        invalidateQuery();
        toast.success("ایستگاه با موفقیت ویرایش شد");
        closeModal();
      });
    },
  });

  const mutation = isEdit ? editStationMutation : addStationMutation;

  useEffect(() => {
    if (modalData) {
      getLocationData({ lat: modalData.lat, lng: modalData.lng }).then((res) => {
        setFormData((prev) => ({ ...prev, address: res.formattedAddress }));
      });

      const tags: ITag[] = modalData?.tags || [];
      setLastLoc({ lat: modalData.lat, lng: modalData.lng, address: modalData.address });
      setFormData({ ...modalData, tags: tags.map((i) => i.name).join("-") });

      if (modalData.attributes) {
        setAttributes(modalData.attributes);
      }
    }
  }, [modalData]);
  useEffect(() => {
    if (!isOpen) {
      setFormData(null);
      setAttributes([]);
      setEditLocMode(false);
    }
  }, [isOpen]);

  const handleChangeLoc = () => {
    dispatch(setMinimize(true));
    dispatch(toggleShowStationMarker());
    // setEditLocMode(true);
    // dispatch(setStation([formData]));
  };

  const handleNext = () => {
    Api.get(`path/station/${modalData.id}`).then((res: any) => {
      const pathsLength = res.data.Data.station.paths.length;
      if (pathsLength < 2) {
        mutation.mutate();
        closeModal();
      } else {
        setStep(2);
        setStationPaths(res.data.Data.station.paths);
      }
    });
  };

  const handlepathRange = (event: React.MouseEvent<HTMLElement>, newRange: "" | "all" | "one") => {
    if (newRange !== null) {
      setPathRange(newRange);
    }
  };

  const deleteStationForOnePath = useMutation({
    mutationFn: () => {
      const pivotId = stationPaths.filter((item) => item.id === path?.id)[0]?.pivot?.id;
      return Api.delete(`path/path_station/${pivotId}`).then((res) => {
        toast.success("ایستگاه از این مسیر با موفقیت حذف شد");
        // setShowConfirm(false);
        // setOpen(false);
        // queryClient.invalidateQueries({ queryKey: [QueryData.STATION.key] });
      });
    },
  });

  const handleEditPath = async () => {
    if (pathRange === "all") {
      closeModal();
      mutation.mutate();
    }
    if (pathRange === "one") {
      const tags = formData?.tags.length > 0 ? formData?.tags.split("-") : [];

      const selectedStation = stationPaths.filter((item) => item.id === path?.id)[0];
      const pivotId = selectedStation?.pivot?.id;
      const stationOrder = selectedStation?.pivot?.order;
      await Api.delete(`path/path_station/${pivotId}`);
      Api.post("path/station", { ...formData, isEdit: false, attributes, type, tags }).then((res) =>
        Api.post("path/path_station", { path_id: path?.id, station_id: res.data.Data.id, order: stationOrder }).then((res2) => {
          toast.success("ایستگاه با موفقیت دراین مسیر ویرایش شد");
          closeModal();
        })
      );
    }
  };

  return (
    <Modal open={showAddStationModal || isOpen} onClose={closeModal} size="md">
      <Card sx={{ p: 3 }}>
        {step === 1 ? (
          <Grid container columnSpacing={3}>
            <Grid item xs={4}>
              <Box
                sx={{
                  width: "100%",
                  height: "87%",
                  borderRadius: 2,
                  overflow: "hidden",
                  marginBottom: "6%",
                  position: "relative",
                }}
              >
                <MapContainer2
                  position={modalData ? { lat: modalData?.lat, lng: modalData?.lng } : null}
                  dragging={true}
                  singleStationData={isEdit ? formData : stationLoc}
                >
                  {" "}
                  <Station2 singleStationData={[formData]} setFormData={setFormData} editLocMode={true} isEdit={isEdit} />
                  <Marker position={[51.51, 30]} icon={destinationIcon} />
                </MapContainer2>

                <AutorenewIcon
                  onClick={handleChangeLoc}
                  sx={{
                    // display: editLocMode ? "none" : "block",
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                  }}
                />
              </Box>
              <Typography>برای جابجایی ایستگاه مارکر را جابجا کنید</Typography>
            </Grid>

            <Grid item xs={8}>
              <Typography variant="h5" fontWeight="800">
                {modalText} {stationData?.text}
              </Typography>

              <FormContainer gridProps={{ spacing: 1.5 }} sx={{ mt: 3 }}>
                <Form inputs={INPUTS} data={formData} setData={setFormData} />
              </FormContainer>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" fontWeight="800">
                ویژگی های ایستگاه
              </Typography>

              <Box sx={{ maxHeight: "200px", overflowY: "scroll", pr: 1, mt: 1, py: 1 }}>
                {attributes.map((attribute, index) => (
                  <Grid container columnSpacing={1} mb={2}>
                    <Grid item xs={4.5}>
                      <TextField fullWidth size="small" value={attribute.name} InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={4.5}>
                      <TextField fullWidth size="small" InputProps={{ readOnly: true }} value={attribute.value} />
                    </Grid>
                    <Grid item xs={3}>
                      <IconButton onClick={() => handleRemoveAttribute(index)}>
                        <DeleteOutlineOutlinedIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}

                <FormContainer gridProps={{ spacing: 1, alignItems: "stretch" }}>
                  <Form inputs={ATTRIBUTE_INPUTS} data={attributeForm} setData={setAttributeForm} gridSize={4.5} size="small" />

                  <Grid item xs={3}>
                    <Button variant="contained" fullWidth onClick={handleAddAttribute} sx={{ height: "100%" }}>
                      افزودن
                    </Button>
                  </Grid>
                </FormContainer>
              </Box>

              <Grid container mt={2} justifyContent="flex-end" spacing={1}>
                <Grid item xs={3}>
                  <Button variant="light" fullWidth size="large" sx={{ fontWeight: "700" }} onClick={handleCloseModal}>
                    انصراف
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <LoadingButton
                    loading={mutation.isLoading}
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={isEdit ? handleNext : () => mutation.mutate()}
                  >
                    {modalText}
                  </LoadingButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : null}
        {step === 2 ? (
          <Grid container columnSpacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="800">
                لیست مسیرهایی که این ایستگاه در آن ها استفاده شده
              </Typography>

              <Box sx={{ maxHeight: "200px", overflowY: "scroll", pr: 1, mt: 1, py: 1 }}>
                {stationPaths.map((p) => (
                  <Box sx={{ pl: 1, mt: 1, py: 1, border: "1px solid #aaa", borderRadius: 1 }}>{p.name}</Box>
                ))}
              </Box>
              <ToggleButtonGroup
                value={pathRange}
                exclusive
                onChange={handlepathRange}
                aria-label="heatMap2-mode"
                sx={{ mt: "10px" }}
              >
                <ToggleButton
                  value="one"
                  aria-label="source-mode"
                  style={{
                    // width: "56px",
                    height: "56px",
                    backgroundColor: pathRange === "one" ? "#4754CA" : "",
                    color: pathRange === "one" ? "#fff" : "",
                  }}
                >
                  فقط در این مسیر اعمال شود
                </ToggleButton>
                <ToggleButton
                  value="all"
                  aria-label="destination-mode"
                  style={{
                    // width: "56px",
                    height: "56px",
                    backgroundColor: pathRange === "all" ? "#4754CA" : "",
                    color: pathRange === "all" ? "#fff" : "",
                  }}
                >
                  در همه مسیرها اعمال شود
                </ToggleButton>
              </ToggleButtonGroup>
              {modalData.code === formData?.code && pathRange === "one" ? (
                <Typography variant="body2" sx={{ mt: "10px", color: "red" }}>
                  لطفا کد مسیر جدید وارد کنید
                </Typography>
              ) : null}

              <Grid container mt={2} justifyContent="flex-end" spacing={1}>
                <Grid item xs={3}>
                  <Button variant="light" fullWidth size="large" sx={{ fontWeight: "700" }} onClick={handleCloseModal}>
                    انصراف
                  </Button>
                </Grid>
                <Grid item xs={3}>
                  <Button variant="light" fullWidth size="large" sx={{ fontWeight: "700" }} onClick={() => setStep(1)}>
                    برگشت
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <LoadingButton loading={mutation.isLoading} variant="contained" fullWidth size="large" onClick={handleEditPath}>
                    اعمال ویرایش
                  </LoadingButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : null}
      </Card>
    </Modal>
  );
}
